import { z } from 'zod';
import { uuidSchema, isoDateTimeSchema } from '../schemas/common';

export const mediaKindSchema = z.enum(['video', 'photo']);
export type MediaKind = z.infer<typeof mediaKindSchema>;

export const mediaUploadStatusSchema = z.enum(['pending', 'uploading', 'complete', 'failed']);
export type MediaUploadStatus = z.infer<typeof mediaUploadStatusSchema>;

// The Edge Function init request body.
export const mediaUploadInitRequestSchema = z
  .object({
    kind: mediaKindSchema,
    dance_id: uuidSchema.optional(),
    competition_routine_id: uuidSchema.optional(),
    content_type: z.string().min(1).max(120),
    size_bytes: z.number().int().positive().max(500_000_000),
    title: z.string().max(500).optional(),
    description: z.string().max(2000).optional(),
  })
  .refine((v) => !!(v.dance_id ?? v.competition_routine_id), {
    message: 'Either dance_id or competition_routine_id must be set',
    path: ['dance_id'],
  });
export type MediaUploadInitRequest = z.infer<typeof mediaUploadInitRequestSchema>;

export const mediaUploadInitResponseSchema = z.object({
  asset_id: uuidSchema,
  upload_url: z.string().url(),
  content_type: z.string(),
  expires_in: z.number().int().positive(),
});
export type MediaUploadInitResponse = z.infer<typeof mediaUploadInitResponseSchema>;

export const mediaUploadCompleteRequestSchema = z.object({
  asset_id: uuidSchema,
  duration_seconds: z.number().int().nonnegative().optional(),
  thumbnail_key: z.string().min(1).max(1024).optional(),
  checksum_sha256: z
    .string()
    .regex(/^[0-9a-f]{64}$/, 'must be a 64-char lowercase hex sha256')
    .optional(),
});
export type MediaUploadCompleteRequest = z.infer<typeof mediaUploadCompleteRequestSchema>;

// Full media row as returned by the API. Some fields are admin-only.
export interface MediaAsset {
  id: string;
  created_at: string;
  uploaded_by: string | null;
  kind: MediaKind;
  storage_key: string;
  content_type: string;
  size_bytes: number;
  dance_id: string | null;
  competition_routine_id: string | null;
  title: string | null;
  description: string | null;
  upload_status: MediaUploadStatus;
  uploaded_at: string | null;
  duration_seconds: number | null;
  thumbnail_key: string | null;
  checksum_sha256: string | null;
}

export const mediaAssetSchema = z.object({
  id: uuidSchema,
  created_at: isoDateTimeSchema,
  uploaded_by: uuidSchema.nullable(),
  kind: mediaKindSchema,
  storage_key: z.string(),
  content_type: z.string(),
  size_bytes: z.number().int().positive(),
  dance_id: uuidSchema.nullable(),
  competition_routine_id: uuidSchema.nullable(),
  title: z.string().nullable(),
  description: z.string().nullable(),
  upload_status: mediaUploadStatusSchema,
  uploaded_at: isoDateTimeSchema.nullable(),
  duration_seconds: z.number().int().nonnegative().nullable(),
  thumbnail_key: z.string().nullable(),
  checksum_sha256: z.string().nullable(),
}) satisfies z.ZodType<MediaAsset>;
